import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setCurrentVet = useAuthStore((state) => state.setCurrentVet);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login({ email, password, remember_me: rememberMe });

      // Store authentication tokens and user info
      setAuth(
        {
          ...response.user,
          role: response.user.role as any,
          is_active: true,
          created_at: new Date().toISOString(),
        },
        response.access_token,
        response.refresh_token
      );

      // Store the vet information
      setCurrentVet(
        response.user.id,
        `Dr. ${response.user.first_name} ${response.user.last_name}`
      );

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setLoading(true);
      if (credentialResponse.credential) {
        const response = await authService.googleLogin(credentialResponse.credential);

        setAuth(
          {
            ...response.user,
            role: response.user.role as any,
            is_active: true,
            created_at: new Date().toISOString(),
          },
          response.access_token,
          response.refresh_token
        );

        setCurrentVet(
          response.user.id,
          `Dr. ${response.user.first_name} ${response.user.last_name}`
        );

        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Google Login Error:', error);
      setError(t('auth.login.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google Login Failed');
    setError(t('auth.login.error'));
  };

  return (
    <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
            <Box
              component="img"
              src="/logo.png"
              alt="SynaVet"
              sx={{
                height: { xs: 44, sm: 56 },
                width: 'auto',
                transition: 'transform 0.3s',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            textAlign="center"
            fontWeight={700}
            sx={{
              letterSpacing: '-0.5px',
              color: '#5D87FF',
              fontSize: { xs: '1.6rem', sm: '2.125rem' },
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            SynaVet
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: { xs: 1.5, sm: 3 } }}>
            {t('auth.login.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Google OAuth Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 }, overflow: 'hidden' }}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              width="220"
              text="signin_with"
            />
          </Box>

          <Divider sx={{ my: { xs: 1, sm: 2 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('auth.signup.or')}
            </Typography>
          </Divider>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="dense"
              required
              fullWidth
              id="email"
              label={t('auth.login.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              label={t('auth.login.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                  disabled={loading}
                />
              }
              label={t('auth.login.rememberMe')}
              sx={{ mt: 0.5 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: { xs: 1.5, sm: 3 }, mb: { xs: 1, sm: 2 } }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.login.submit')}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: { xs: 1, sm: 2 } }}>
            <Typography variant="body2">
              {t('auth.login.noAccount')}{' '}
              <Link
                to="/signup"
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                {t('auth.login.signup')}
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <Link
                to="/forgot-password"
                style={{ color: 'inherit' }}
              >
                {t('auth.forgotPassword.title')}
              </Link>
            </Typography>
          </Box>
    </>
  );
};

